/**
 * Shared CRE-aware value formatter for chat change chips and the change
 * walkthrough wizard. Formats any raw change value (number, string, object)
 * based on the field name it belongs to. Never emits raw JSON.
 */

import { prettyValue, prettyFieldLabel } from "./labels";
import {
  fmtCurrency,
  fmtCurrencyCompact,
  fmtPercent,
  fmtPSF,
  fmtSF,
  fmtDecimal,
} from "./format";

/** Percentage fields (stored as decimals: 0.05 = 5%). */
const PCT_FIELDS = [
  "vacancy_rate_pct", "credit_loss_pct", "expense_growth_rate_pct",
  "market_rent_growth_pct_annual", "tax_growth_rate_pct", "insurance_growth_rate_pct",
  "going_in_cap_rate", "exit_cap_rate", "discount_rate", "interest_rate",
  "selling_costs_pct", "management_fee_pct", "general_vacancy_pct",
  "renewal_probability_pct", "leasing_commissions_new_pct",
  "leasing_commissions_renewal_pct", "origination_fee_pct", "committed_lc_pct",
  "annual_rent_increase_pct", "renewal_market_rent_pct",
  "other_income_growth_pct", "cpi_rate",
  // CAM sub-fields that are percentages
  "expense_cap_pct", "cap_cpi_spread", "cap_floor_pct",
  "tax_expense_cap_pct", "tax_admin_fee_pct",
  "insurance_expense_cap_pct", "insurance_admin_fee_pct",
  "fixed_cam_psf_escalation_pct", "stop_escalation_pct",
  "tax_stop_escalation_pct", "insurance_stop_escalation_pct",
];

/** Fields with 2-decimal percent display (cap rates, IRR, interest). */
const PCT_2DEC_FIELDS = ["going_in_cap_rate", "exit_cap_rate", "discount_rate", "interest_rate"];

/** Per-square-foot dollar fields. */
const PSF_FIELDS = [
  "base_rent_annual_psf", "capital_reserves_psf", "market_rent_psf",
  "new_tenant_ti_psf", "renewal_tenant_ti_psf",
  "committed_ti_psf", "fixed_cam_psf", "stop_amount_psf", "rent_psf",
];

/** Square-footage fields — must render "2,000 SF", never "$2,000". */
const SF_FIELDS = ["leased_sf", "total_leasable_area", "gross_building_area", "square_footage"];

/** Whole-dollar amount fields (OpEx categories, loan amounts, stops, base year amounts). */
const DOLLAR_FIELDS = [
  "purchase_price", "loan_amount", "npv", "exit_price",
  "real_estate_taxes", "insurance", "utilities", "repairs_and_maintenance",
  "janitorial", "landscaping", "administrative", "capital_expenditures",
  "reserves", "security", "expense_stop_amount", "base_year_amount",
  "tax_base_year_amount", "insurance_base_year_amount",
  "annual_amount", "amount", "security_deposit",
];

/** Duration fields measured in years. */
const YEARS_FIELDS = ["hold_period_years", "amortization_years", "lease_term_years", "io_period_years"];

/** Calendar-year fields displayed as a bare integer (e.g. 2024). */
const CALENDAR_YEAR_FIELDS = ["base_year", "tax_base_year", "insurance_base_year", "year"];

/** Duration fields measured in months. */
const MONTHS_FIELDS = [
  "free_rent_months", "downtime_months", "renewal_downtime_months",
  "new_tenant_free_rent_months", "renewal_free_rent_months",
  "absorption_months", "period_months",
];

/** Strip prefixes/year-suffix to get the base field name for matching. */
function baseFieldName(fieldName: string): string {
  return fieldName
    .replace(/^rollover_assumptions\./, "")
    .replace(/^rollover_override\./, "")
    .replace(/^cam_structure\./, "")
    .replace(/^debt\./, "")
    .replace(/^override\./, "")
    .replace(/^opex\./, "")
    .replace(/^expense_override\./, "")
    .replace(/^profile\./, "")
    .replace(/\.\d+$/, ""); // strip year suffix
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Pretty CAM type + key economics, e.g. "Base Year Stop (Base Year 2024, 5.0% cap)". */
function fmtCamStructure(cam: Record<string, unknown>): string {
  const type = prettyValue(String(cam.cam_type ?? "")) || "CAM";
  const parts: string[] = [];
  if (cam.base_year != null) parts.push(`Base Year ${cam.base_year}`);
  if (typeof cam.base_year_amount === "number" && cam.base_year_amount > 0)
    parts.push(`${fmtCurrency(cam.base_year_amount)} base`);
  if (typeof cam.expense_cap_pct === "number" && cam.expense_cap_pct > 0)
    parts.push(`${fmtPercent(cam.expense_cap_pct)} cap`);
  if (typeof cam.fixed_cam_psf === "number" && cam.fixed_cam_psf > 0)
    parts.push(`${fmtPSF(cam.fixed_cam_psf)} fixed`);
  if (typeof cam.management_fee_pct === "number" && cam.management_fee_pct > 0)
    parts.push(`${fmtPercent(cam.management_fee_pct)} admin fee`);
  if (typeof cam.expense_stop_amount === "number" && cam.expense_stop_amount > 0)
    parts.push(`${fmtCurrency(cam.expense_stop_amount)} stop`);
  return parts.length ? `${type} (${parts.join(", ")})` : type;
}

/** Tenant payload (e.g. _add_tenant): "Name — 2,000 SF @ $24.00/SF". */
function fmtTenantPayload(t: Record<string, unknown>): string {
  const name = String(t.tenant_name ?? "Tenant");
  const pieces: string[] = [];
  if (typeof t.leased_sf === "number" && t.leased_sf > 0) pieces.push(fmtSF(t.leased_sf));
  if (typeof t.base_rent_annual_psf === "number" && t.base_rent_annual_psf > 0)
    pieces.push(`@ ${fmtPSF(t.base_rent_annual_psf)}`);
  return pieces.length ? `${name} — ${pieces.join(" ")}` : name;
}

/** Generic object fallback: "Label: value, Label: value" — never raw JSON. */
function fmtGenericObject(obj: Record<string, unknown>): string {
  const entries = Object.entries(obj).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  if (!entries.length) return "—";
  const shown = entries
    .slice(0, 4)
    .map(([k, v]) => `${prettyFieldLabel(k)}: ${fmtFieldValue(v, k)}`);
  return shown.join(", ") + (entries.length > 4 ? ", …" : "");
}

/** CRE-aware value formatting based on field name. */
export function fmtFieldValue(v: unknown, fieldName: string): string {
  if (v === null || v === undefined) return "—";

  if (Array.isArray(v)) {
    if (!v.length) return "—";
    return v.map((item) => fmtFieldValue(item, fieldName)).join("; ");
  }

  if (isRecord(v)) {
    // cam_structure objects — pretty CAM type + key economics
    if ("cam_type" in v) return fmtCamStructure(v);
    // tenant payloads (_add_tenant) — "Name — 2,000 SF @ $24.00/SF"
    if (fieldName === "_add_tenant" || ("tenant_name" in v && "leased_sf" in v))
      return fmtTenantPayload(v);
    // gross-dollar overrides {value, type} → "$1,000,000"
    if ("value" in v && "type" in v && typeof v.value === "number")
      return fmtCurrency(v.value);
    return fmtGenericObject(v);
  }

  if (typeof v === "boolean") return v ? "Yes" : "No";
  // Bare enum-ish strings (snake_case) route through prettyValue;
  // names, dates, and amounts pass through unchanged.
  if (typeof v === "string") return prettyValue(v);
  if (typeof v !== "number") return String(v);

  const n = v;
  const base = baseFieldName(fieldName);

  if (PCT_FIELDS.includes(base)) {
    return fmtPercent(n, PCT_2DEC_FIELDS.includes(base) ? 2 : 1);
  }

  if (PSF_FIELDS.includes(base)) return fmtPSF(n);

  // Square footage — checked before any dollar fallback
  if (SF_FIELDS.includes(base)) return fmtSF(n);

  if (DOLLAR_FIELDS.includes(base)) return n >= 1_000_000 ? fmtCurrencyCompact(n) : fmtCurrency(n);

  if (YEARS_FIELDS.includes(base)) return `${n} ${n === 1 ? "year" : "years"}`;

  // Calendar year (e.g. 2024) displayed as a plain integer
  if (CALENDAR_YEAR_FIELDS.includes(base)) return String(n);

  if (MONTHS_FIELDS.includes(base)) return `${n} ${n === 1 ? "month" : "months"}`;

  // Rent escalation value — context-dependent (could be % or $ PSF)
  if (base === "rent_escalation_value") {
    return n > 0 && n < 1 ? fmtPercent(n, 2) : fmtPSF(n);
  }

  // Fallback: small decimals likely percentages, large numbers likely dollars
  if (n !== 0 && Math.abs(n) < 1) return fmtPercent(n);
  if (Math.abs(n) >= 1000) return fmtCurrency(n);
  return fmtDecimal(n);
}
